// =============================================================================
// SHOPPING BAG MODAL COMPONENT
// =============================================================================

import React, { useState } from 'react';
import { X, Plus, Minus, Trash2, ShoppingBag } from 'lucide-react';
import { useShoppingBag } from '@/contexts/ShoppingBagContext';
import { Button } from '@/components/Button';
import { paymentService } from '@/services/payment';
import { useToast } from '@/contexts/ToastContext';
import './ShoppingBagModal.scss';

export const ShoppingBagModal: React.FC = () => {
  const { bag, isOpen, closeBag, removeItem, updateQuantity, clearBag } = useShoppingBag();
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const toast = useToast();

  if (!isOpen) return null;

  const handleCheckout = async () => {
    if (bag.items.length === 0) {
      toast.error('Your shopping bag is empty');
      return;
    }

    setIsCheckingOut(true);

    try {
      // Create checkout session with all items
      // Items are stored in metadata for order tracking
      // Note: For better Stripe reporting, consider updating backend to support multiple line_items
      const response = await paymentService.createCheckoutSession({
        paymentConfig: {
          type: 'one-time',
          category: 'professional-services',
          amount: bag.totalAmount,
          currency: 'usd',
          name: 'Awani Shopping Bag',
          description: `${bag.totalItems} item${bag.totalItems !== 1 ? 's' : ''} from Awani Collections`,
          metadata: {
            checkoutType: 'shopping-bag',
            items: JSON.stringify(bag.items.map((item) => ({
              id: item.id,
              categoryName: item.categoryName,
              productName: item.productName,
              price: item.price,
              priceDisplay: item.priceDisplay,
              quantity: item.quantity,
              size: item.size,
              image: item.image,
              description: item.description || '',
              categoryDescription: item.categoryDescription || '',
              sku: item.sku || '',
              addedAt: item.addedAt || Date.now(),
            }))),
            totalItems: bag.totalItems.toString(),
            totalAmount: bag.totalAmount.toString(),
          },
        },
        successUrl: `${window.location.origin}/payment/success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}?bag=canceled`,
      });

      // Note: Bag will be cleared after successful payment confirmation
      // This allows users to return to their bag if checkout is cancelled
      
      // Redirect to Stripe Checkout
      window.location.href = response.url;
    } catch (error) {
      // Checkout error
      toast.error(error instanceof Error ? error.message : 'Failed to initiate checkout');
      setIsCheckingOut(false);
    }
  };

  const handleRemoveItem = (itemId: string) => {
    removeItem(itemId);
    toast.success('Item removed from bag');
  };

  const handleQuantityChange = (itemId: string, currentQuantity: number, delta: number) => {
    const newQuantity = Math.max(1, currentQuantity + delta);
    updateQuantity(itemId, newQuantity);
  };

  return (
    <div className="shopping-bag-modal-overlay" onClick={closeBag}>
      <div className="shopping-bag-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            <ShoppingBag size={24} />
            <h2>Shopping Bag</h2>
            {bag.totalItems > 0 && (
              <span className="item-count">({bag.totalItems} item{bag.totalItems !== 1 ? 's' : ''})</span>
            )}
          </div>
          <button
            className="modal-close"
            onClick={closeBag}
            aria-label="Close shopping bag"
            type="button"
          >
            <X size={24} />
          </button>
        </div>

        <div className="modal-content">
          {bag.items.length === 0 ? (
            <div className="empty-bag">
              <ShoppingBag size={64} className="empty-icon" />
              <h3>Your bag is empty</h3>
              <p>Add items from our catalog to get started</p>
              <Button variant="primary" onClick={closeBag}>
                Continue Shopping
              </Button>
            </div>
          ) : (
            <>
              <div className="bag-items">
                {bag.items.map((item) => (
                  <div key={item.id} className="bag-item">
                    <div className="item-image">
                      <img src={item.image} alt={item.productName} loading="lazy" />
                    </div>
                    <div className="item-details">
                      <div className="item-header">
                        <h3 className="item-name">{item.categoryName}</h3>
                        <button
                          className="item-remove"
                          onClick={() => handleRemoveItem(item.id)}
                          aria-label={`Remove ${item.productName}`}
                          type="button"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                      <p className="item-product">{item.productName}</p>
                      {item.size && (
                        <p className="item-size">Size: {item.size}</p>
                      )}
                      <div className="item-footer">
                        <div className="quantity-controls">
                          <button
                            className="quantity-btn"
                            onClick={() => handleQuantityChange(item.id, item.quantity, -1)}
                            aria-label="Decrease quantity"
                            type="button"
                          >
                            <Minus size={16} />
                          </button>
                          <span className="quantity-value">{item.quantity}</span>
                          <button
                            className="quantity-btn"
                            onClick={() => handleQuantityChange(item.id, item.quantity, 1)}
                            aria-label="Increase quantity"
                            type="button"
                          >
                            <Plus size={16} />
                          </button>
                        </div>
                        <span className="item-price">{item.priceDisplay}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bag-summary">
                <div className="summary-row">
                  <span>Subtotal</span>
                  <span>{bag.totalAmountDisplay}</span>
                </div>
                <div className="summary-row total">
                  <span>Total</span>
                  <span>{bag.totalAmountDisplay}</span>
                </div>
              </div>
            </>
          )}
        </div>

        {bag.items.length > 0 && (
          <div className="modal-footer">
            <Button
              variant="primary"
              size="lg"
              onClick={handleCheckout}
              loading={isCheckingOut}
              disabled={isCheckingOut}
              className="checkout-button"
            >
              {isCheckingOut ? 'Processing...' : `Checkout ${bag.totalAmountDisplay}`}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShoppingBagModal;

